import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Check, Lock, Play, Timer, Trophy } from 'lucide-react';
import UnitStatus from './components/UnitStatus';
import { Button, useToast } from '../TeacherDashboardV3/components/ui';
import { BUFFALO } from '../TeacherDashboardV3/assets';
import { DOMAIN_TOOL_ART, DOMAIN_TOOL_TAGLINES } from './assets/domainArt';
import {
  DOMAINS, DOMAIN_GUIDES, domainOf, STUDENT, UNPLACED,
  partsFor, MAX_LEVEL, minLevelOf, progressOf, levelInfo, testSessionUrl, engineSessionUrl,
  unitAssignmentsFor, assignmentPartIndex,
} from './data/studentMock';
import { useMyProgress, fetchDomainUnits, useStudentAgenda } from './data/api';
import { isOpenAssignmentSlot } from './data/assignmentSlots';
import { hasPlacement } from './data/placementApi';
import { useAuth } from '../Accounts/AuthContext';
import { CubeLoader } from '../pages/LoadingSpinnerPage';
import CheckLockTimer from './components/CheckLockTimer';
import { dueDateOf, dueEndMs, shortDue } from '../TeacherDashboardV3/data/unitPlan';
import './sdv3.css';

// Build the road's node list from the REAL unit windows. SIMPLIFIED (Sandy
// 2026-08-12): each skill is ONE coin named after the skill itself — the
// engine still runs Overview/Warm-up/Lesson/Practice inside it, but the road
// doesn't spell that out. Then the unit's Mixed practice and Skill check.
// Skill names arrive as title-cased slugs ("Command Of Evidence Quantitative");
// lowercase the connector words so they read like names, not identifiers.
const CONNECTORS = new Set(['of', 'and', 'the', 'a', 'an', 'from', 'to', 'on', 'in', 'with', 'for', 'or']);
const humanName = (s) => String(s || '').split(' ').filter(Boolean)
  .map((w, i) => (i > 0 && CONNECTORS.has(w.toLowerCase()) ? w.toLowerCase() : w))
  .join(' ');

function partsFromUnits(units) {
  const parts = [];
  units.forEach((u) => {
    u.items.forEach((it, j) => {
      // Coin = bold "Lesson N" + the skill's REAL name as a small clamped
      // subline (Sandy 2026-08-14: scannable label, readable name, no blob).
      const name = humanName(it.label) + (it.borrowed ? ' (review)' : '');
      const label = u.items.length > 1 ? `Lesson ${j + 1}` : 'Lesson';
      parts.push({ key: `u${u.n}-s${j}`, label, sub: name, concept: name, blurb: `${name}: the lesson, then practice it on your own` });
    });
    parts.push({ key: `u${u.n}-mixed`, label: 'Mixed practice', concept: `Unit ${u.n}`, blurb: 'Everything in this unit, shuffled together' });
    parts.push({ key: `u${u.n}-check`, label: 'Skill check', concept: `Unit ${u.n}`, blurb: 'Pass to finish this unit — no help on this one' });
  });
  return parts;
}

// Where the student is on that road, from the engine's unitProgress
// ({a1..a4} per current unit: skill 1 counts to 4, skill 2 to 4, mixed and
// check to 1). With one coin per skill, the current coin is simply the first
// segment the engine hasn't finished.
function partIndexFromProgress(units, unitIdx, unitProgress) {
  let idx = 0;
  for (let i = 0; i < Math.min(unitIdx || 0, units.length); i++) {
    idx += units[i].items.length + 2; // whole finished unit
  }
  const u = units[Math.min(unitIdx || 0, Math.max(0, units.length - 1))];
  const up = unitProgress || {};
  const segs = [(up.a1 || 0) >= 4];
  if ((u?.items?.length || 0) > 1) segs.push((up.a2 || 0) >= 4);
  segs.push((up.a3 || 0) >= 1, (up.a4 || 0) >= 1);
  for (const done of segs) {
    if (!done) break;
    idx += 1;
  }
  return idx;
}

const CARD = 'bg-white border border-neutral-200 rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,.04)]';

// Per-subject page theming, Brilliant-style: one accent pair colors the level
// pill, the current road ring, and the action button. Reading wears the gold
// pair from its tool art (strong fills/text, bright outlines); math stays blue.
const SUBJECT_ACCENTS = {
  // Reading wears the LIGHT gold throughout (the deep #C29008 read muddy);
  // its ring pairs the gold with a paler tint, and gold surfaces carry ink
  // text. Math keeps the blue pair with white text.
  reading: {
    main: '#F8C12A', mainText: '#141414', line: '#F8C12A', ringLight: '#FFDF85',
    soft: 'rgba(248, 193, 42, 0.45)', btn: '#F8C12A', btnText: '#141414', btnRim: '#C29008',
  },
  math: {
    main: '#2563EB', mainText: '#ffffff', line: '#93C5FD', ringLight: '#93C5FD',
    soft: 'rgba(59, 130, 246, 0.30)', btn: '#2563EB', btnText: '#ffffff', btnRim: '#1E40AF',
  },
};

// Bottom-card row label: generic part names get their concept ("Warm-up · Details",
// "Lesson 1 · Command of Evidence Quantitative"), so the row makes sense out of
// road context.
const partRowLabel = (p) =>
  p.concept && p.concept !== p.label ? `${p.label} · ${p.concept}` : p.label;

// Horizontal wander of the road: everything hangs LEFT with a gentle wiggle
// (Sandy 2026-08-12: the centered zigzag with side-swapping labels read as
// wonky). Labels always sit to the coin's right.
const PATH_X = [0, 44, 14, 52, 26, 8, 38, 18];

// One coin on the road. status: 'done' | 'current' | 'locked'
function PathNode({ part, status, isLast, onClick, unreviewable = false }) {
  const coin =
    status === 'done' ? (
      <span className="sdv3-disc sdv3-disc-done">
        <Check size={19} strokeWidth={3} />
      </span>
    ) : status === 'current' ? (
      <span className="sdv3-disc sdv3-disc-current">
        <Play size={21} strokeWidth={2.5} fill="currentColor" />
      </span>
    ) : (
      <span className="sdv3-disc sdv3-disc-locked">
        {isLast ? <Trophy size={17} strokeWidth={2.5} /> : <Lock size={15} strokeWidth={2.5} />}
      </span>
    );

  // Brilliant-style labels: one quiet line; only the current node reads in ink.
  // A due date rides its own coin's label (Sandy 2026-08-12: no more separate
  // break rows): gold normally, red once it's past due or under a day out.
  const label = (
    <span className="flex flex-col gap-1 max-w-[170px]">
      <span
        className={
          'text-[14px] leading-snug ' +
          (status === 'current' ? 'text-ink font-semibold' :
           status === 'done' ? 'text-neutral-500 font-medium' : 'text-neutral-400 font-medium')
        }
      >
        {part.label}
      </span>
      {part.sub && (
        <span
          className={
            'text-[11.5px] leading-snug font-medium ' +
            (status === 'current' ? 'text-neutral-500' : 'text-neutral-400')
          }
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {part.sub}
        </span>
      )}
      {part.due && (
        <span className={
          'text-[11.5px] font-bold leading-snug ' +
          (part.due.urgent ? 'text-error-600' : 'text-gold-700')
        }>
          {part.due.past ? 'Was due ' : 'Due '}{part.due.text}
        </span>
      )}
      {part.timer && (
        <span
          className={
            'flex items-center gap-1 w-fit text-[11px] font-bold rounded-[6px] px-1.5 py-0.5 ' +
            (status === 'locked' ? 'bg-neutral-100 text-neutral-400' : 'bg-gold-100 text-gold-700')
          }
          title="The timer starts when you reach the mastery check"
        >
          <Timer size={11} strokeWidth={2.5} /> {part.timer}
        </span>
      )}
    </span>
  );

  // Current part starts; finished parts open for review. Locked parts don't react.
  const clickable = (status === 'current' || status === 'done') && onClick;
  const Row = clickable ? 'button' : 'div';
  return (
    <div className="flex justify-start mt-8 first:mt-5 sm:mt-9 sm:first:mt-6 pl-2">
      <Row
        onClick={clickable ? onClick : undefined}
        title={status === 'done' && !unreviewable ? `Review: ${part.blurb}` : part.blurb}
        className={
          'flex items-center gap-4 border-none bg-transparent p-0 text-left ' +
          (clickable ? 'cursor-pointer' : unreviewable && status === 'done' ? 'cursor-not-allowed' : '')
        }
        style={{ transform: `translateX(${PATH_X[part.i % PATH_X.length]}px)` }}
      >
        {coin}
        {label}
      </Row>
    </div>
  );
}

// The windy road line (Sandy 2026-08-12): one soft gray path snaking through
// every coin, drawn from the coins' real positions so it follows the zigzag
// exactly. Sits under the coins (z-0 vs their z-[1]); never blocks clicks.
function RoadLine({ containerRef, count }) {
  const [geo, setGeo] = useState(null);
  // useEffect, NOT useLayoutEffect: this component is a CHILD of the div that
  // carries containerRef, and layout effects run child-before-parent — so on
  // first mount the ref is still null there. Passive effects run after every
  // ref is attached.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const pts = [];
      for (let i = 0; i < count; i += 1) {
        const disc = el.querySelector(`[data-road-i="${i}"] .sdv3-disc`);
        if (!disc) break;
        const c = el.getBoundingClientRect();
        const r = disc.getBoundingClientRect();
        pts.push({ x: r.left - c.left + r.width / 2, y: r.top - c.top + r.height / 2 });
      }
      if (pts.length < 2) { setGeo(null); return; }
      // Smooth S-curves between coins: each segment eases out of one coin
      // and into the next through the vertical midpoint.
      let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
      for (let i = 1; i < pts.length; i += 1) {
        const a = pts[i - 1];
        const b = pts[i];
        const my = (a.y + b.y) / 2;
        d += ` C ${a.x.toFixed(1)} ${my.toFixed(1)}, ${b.x.toFixed(1)} ${my.toFixed(1)}, ${b.x.toFixed(1)} ${b.y.toFixed(1)}`;
      }
      const box = el.getBoundingClientRect();
      setGeo({ d, w: box.width, h: box.height });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [containerRef, count]);
  if (!geo) return null;
  return (
    <svg
      viewBox={`0 0 ${geo.w} ${geo.h}`}
      preserveAspectRatio="none"
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}
      aria-hidden="true"
    >
      <path d={geo.d} fill="none" stroke="#e5e5e5" strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  );
}

// The retry loop, drawn on the road: after a failed skill check the teacher
// releases the student to REDO a lesson, and this overlay shows that path —
// (1) an arrow from the Skill check back up to the lesson being redone, then
// (2) an arrow from that lesson back down to the check. Faint by design: low
// contrast strokes, small circled step labels, never blocking clicks. During
// the recheck stage step 1 turns into a green check and step 2 leads.
function RetryLoopOverlay({ containerRef, lessonIdx, checkIdx, stage }) {
  const [geo, setGeo] = useState(null);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const lesson = el.querySelector(`[data-road-i="${lessonIdx}"] .sdv3-disc`);
      const check = el.querySelector(`[data-road-i="${checkIdx}"] .sdv3-disc`);
      if (!lesson || !check) {
        // Breadcrumb, not noise: fires only when the loop SHOULD draw but its
        // anchors are missing (tells us which index came up empty).
        console.warn('[retry-arrows] anchors missing', { lessonIdx, checkIdx, lesson: !!lesson, check: !!check });
        setGeo(null);
        return;
      }
      const c = el.getBoundingClientRect();
      const lr = lesson.getBoundingClientRect();
      const cr = check.getBoundingClientRect();
      setGeo({
        w: c.width,
        h: c.height,
        lx: lr.left - c.left + lr.width / 2,
        ly: lr.top - c.top + lr.height / 2,
        lHalf: lr.width / 2,
        cx: cr.left - c.left + cr.width / 2,
        cy: cr.top - c.top + cr.height / 2,
        cHalf: cr.width / 2,
      });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [containerRef, lessonIdx, checkIdx, stage]);

  if (!geo) return null;
  const GAP = 8; // breathing room between a coin's edge and the arrow tip
  const recheck = stage === 'recheck';
  // Arrow 1 (check -> lesson) bows out to the RIGHT of the road, past the
  // coins; arrow 2 (lesson -> check) bows out to the LEFT. Bows clamp to the
  // container so narrow screens never clip them.
  const upStart = { x: geo.cx + geo.cHalf + GAP, y: geo.cy };
  const upEnd = { x: geo.lx + geo.lHalf + GAP, y: geo.ly };
  const bowR = Math.min(geo.w - 6, Math.max(upStart.x, upEnd.x) + 110);
  const downStart = { x: geo.lx - geo.lHalf - GAP, y: geo.ly };
  const downEnd = { x: geo.cx - geo.cHalf - GAP, y: geo.cy };
  const bowL = Math.max(6, Math.min(downStart.x, downEnd.x) - 110);
  // Label spots: the quadratic's own midpoint (t = 0.5), so each circled step
  // number rides its curve.
  const midY = (geo.cy + geo.ly) / 2;
  const lab1 = { x: 0.25 * upStart.x + 0.5 * bowR + 0.25 * upEnd.x, y: midY };
  const lab2 = { x: 0.25 * downStart.x + 0.5 * bowL + 0.25 * downEnd.x, y: midY };
  const stroke1 = recheck ? '#a7f3d0' : '#93c5fd';
  const stroke2 = recheck ? '#60a5fa' : '#93c5fd';
  return (
    <svg
      viewBox={`0 0 ${geo.w} ${geo.h}`}
      preserveAspectRatio="none"
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 5 }}
      aria-hidden="true"
    >
      <defs>
        <marker id="sdv3-retry-arrow-1" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L8,4 L0,8 Z" fill={stroke1} />
        </marker>
        <marker id="sdv3-retry-arrow-2" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L8,4 L0,8 Z" fill={stroke2} />
        </marker>
      </defs>
      {/* (1) check -> lesson: redo this one */}
      <path
        d={`M ${upStart.x} ${upStart.y} Q ${bowR} ${midY} ${upEnd.x} ${upEnd.y}`}
        fill="none"
        stroke={stroke1}
        strokeWidth="2"
        strokeDasharray="5 5"
        strokeLinecap="round"
        markerEnd="url(#sdv3-retry-arrow-1)"
        opacity={recheck ? 0.55 : 0.9}
      />
      {/* (2) lesson -> check: then retake it */}
      <path
        d={`M ${downStart.x} ${downStart.y} Q ${bowL} ${midY} ${downEnd.x} ${downEnd.y}`}
        fill="none"
        stroke={stroke2}
        strokeWidth="2"
        strokeDasharray="5 5"
        strokeLinecap="round"
        markerEnd="url(#sdv3-retry-arrow-2)"
        opacity={0.9}
      />
      <circle cx={lab1.x} cy={lab1.y} r="10" fill="#ffffff" stroke={recheck ? '#6ee7b7' : stroke1} strokeWidth="1.5" />
      <text x={lab1.x} y={lab1.y} textAnchor="middle" dominantBaseline="central" fontSize="11" fontWeight="700" fill={recheck ? '#059669' : '#3b82f6'}>
        {recheck ? '✓' : '1'}
      </text>
      <circle cx={lab2.x} cy={lab2.y} r="10" fill="#ffffff" stroke={stroke2} strokeWidth="1.5" />
      <text x={lab2.x} y={lab2.y} textAnchor="middle" dominantBaseline="central" fontSize="11" fontWeight="700" fill={recheck ? '#2563eb' : '#3b82f6'}>
        2
      </text>
    </svg>
  );
}

// The domain roadmap, now a PANEL under the home page's subject tabs (Sandy
// 2026-08-12): clicking a domain card swaps the subject path for this road,
// same look as the old standalone page, just embedded. It keeps its own data
// hooks (progress + agenda + unit windows) and holds only its own area on the
// cube while they load. The old /student-dashboard/:subject/:domainKey route
// redirects into it (default export below) so deep links keep working.
export function DomainPanel({ subject, domainKey }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const toast = useToast();

  const domain = DOMAINS[subject] ? domainOf(subject, domainKey) : null;
  // Placement preview: ?unplaced=1 shows the pre-diagnostic state (mock default stays placed).
  const unplaced = UNPLACED || new URLSearchParams(search).get('unplaced') === '1';

  // REAL engine state: the moment a signed-in student's progress loads, it
  // drives this page — placed or not (Sandy 2026-07-27: gating on `placed`
  // sent never-placed real accounts to the demo mock and its ?test=1 session
  // links, so nothing they did was recorded). Mock backs signed-out previews.
  const { user, handleAPIResponse } = useAuth();
  // The road container, measured by the retry-loop overlay below. Declared
  // with the other hooks so the early returns never skip it.
  const roadRef = useRef(null);
  const { data: realAll, loading: progressLoading } = useMyProgress();
  const { data: agenda, loading: agendaLoading } = useStudentAgenda();
  const rd = realAll ? (realAll.domains?.[domainKey] ?? null) : undefined; // undefined = use mock (signed-out); null/null-band = truly unplaced
  // Lock state compares against a STATE clock, not render-time Date.now():
  // the panel used to compute lockActive once and never re-render when the
  // countdown hit zero, so the "Skill check locked" card stuck until the
  // student left and came back (QA 2026-08-27). A self-rescheduling timeout
  // ticks the clock every ≤30s while a lock is pending (so a due-capped lock
  // flips within 30s too) and once more just past the raw expiry.
  const [lockNow, setLockNow] = useState(() => Date.now());
  useEffect(() => {
    const t = rd?.checkLockUntil ? new Date(rd.checkLockUntil).getTime() : NaN;
    if (!Number.isFinite(t)) return undefined;
    const ms = t - Date.now();
    if (ms <= 0) return undefined;
    const id = setTimeout(() => setLockNow(Date.now()), Math.min(Math.max(ms + 500, 1000), 30000));
    return () => clearTimeout(id);
  }, [rd?.checkLockUntil, lockNow]);
  // null = not fetched yet (cube loader holds the page); [] = fetch failed or
  // no content (honest empty road, never the demo mock's parts).
  const [realUnits, setRealUnits] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!realAll || rd?.band == null) { setRealUnits(null); return undefined; }
    fetchDomainUnits({ user, handleAPIResponse }, domainKey, rd.band)
      .then((u) => { if (alive) setRealUnits(u); })
      .catch(() => { if (alive) setRealUnits([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realAll, rd?.band, domainKey, user]);

  // The road shows ONLY the current unit's window (2 skills max) — the path
  // the teacher expects this unit, not the whole band. Position is within it.
  const curUnitIdx = Math.min(Number(rd?.unitIdx || 0), Math.max(0, (realUnits?.length || 1) - 1));
  const curUnit = realUnits ? realUnits[curUnitIdx] : null;
  const mockProg = progressOf(domainKey, unplaced);
  // Signed in with no progress (fetch failed): level null -> the empty state
  // below says so honestly. The mock only ever backs signed-out previews.
  const prog = realAll
    ? {
        level: rd?.band ?? null,
        part: curUnit ? partIndexFromProgress([curUnit], 0, rd?.unitProgress) : 0,
      }
    : user ? { level: null, part: 0 } : mockProg;
  // Which level's road is on screen; defaults to the one the student is on.
  // ?level=N deep-links a specific level (the subject-page ladders use it to
  // look back at finished levels).
  // Content floor: Level 1 does not exist for info/craft/expression, so no
  // deep link, default or chip can ever land the road there.
  const minLevel = minLevelOf(domainKey);
  const [viewLevel, setViewLevel] = useState(() => {
    const q = parseInt(new URLSearchParams(search).get('level'), 10);
    return q >= minLevel && q <= MAX_LEVEL ? q : Math.max(prog.level || minLevel, minLevel);
  });
  // When the real band arrives after mount (async), snap the road to it unless
  // the URL pinned a specific level.
  useEffect(() => {
    const q = parseInt(new URLSearchParams(search).get('level'), 10);
    if (!(q >= minLevel && q <= MAX_LEVEL) && prog.level != null) setViewLevel(Math.max(prog.level, minLevel));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prog.level]);

  // PAST levels get the real unit windows too (Sandy 2026-08-14): the road used
  // to fall back to the demo mock's parts there, and mock coins carry no review
  // target — so clicking any done coin opened review at the first lesson instead
  // of the clicked part. Cached per level so revisiting a level never refetches,
  // and while a level's fetch is in flight the road renders EMPTY, never the
  // mock (the long mock road flashing before the real one was the bug's second
  // half). [] = fetch failed / no content -> mock fallback, old behavior.
  const [viewUnitsByLevel, setViewUnitsByLevel] = useState({});
  const viewUnitsReq = useRef(new Set());
  useEffect(() => { viewUnitsReq.current = new Set(); setViewUnitsByLevel({}); }, [domainKey]);
  useEffect(() => {
    if (!realAll || rd?.band == null || !(viewLevel < rd.band)) return;
    const lvl = viewLevel;
    if (viewUnitsReq.current.has(lvl)) return;
    viewUnitsReq.current.add(lvl);
    fetchDomainUnits({ user, handleAPIResponse }, domainKey, lvl)
      .then((u) => setViewUnitsByLevel((m) => ({ ...m, [lvl]: u })))
      .catch(() => setViewUnitsByLevel((m) => ({ ...m, [lvl]: [] })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realAll, rd?.band, viewLevel, domainKey, user]);
  const viewUnits = viewUnitsByLevel[viewLevel] ?? null;

  // Unknown subject/domain in the URL -> back to the student home.
  useEffect(() => {
    if (!domain) navigate('/student-dashboard', { replace: true });
  }, [domain, navigate]);
  if (!domain) return null;

  // Signed in: hold the page on the brand cube until progress, agenda, AND the
  // placed band's unit windows land — never a flash of the demo mock's road.
  const unitsPending = !!realAll && rd?.band != null && realUnits == null;
  if (user && (progressLoading || agendaLoading || unitsPending)) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <CubeLoader size={120} />
      </div>
    );
  }

  // No level in this domain yet -> designed empty state: centered art, one bold
  // line, one quiet line, one CTA (law 7). A signed-in account goes to the
  // domain's placement walk FIRST (Sandy 2026-08-27: placement is always the
  // first thing in a domain) — the finished walk writes the level into the
  // engine profile, so practice starts where the check placed them. The engine
  // self-place (START_BAND) survives only as the fallback for a domain with no
  // walk; the signed-out preview keeps pointing at the diagnostic.
  if (prog.level == null) {
    const placementUrl = !!user && !!realAll && hasPlacement(domainKey)
      ? `/placement/${domainKey}` : null;
    const startReal = !!realAll && !!engineSessionUrl(domainKey);
    // Signed in but the progress fetch failed: an honest retry state — never
    // the mock, never a misleading "take the diagnostic".
    const loadFailed = !!user && !realAll;
    return (
      <div className="pt-6 pb-10 max-w-[400px] w-full mx-auto flex flex-col items-center text-center">
        <img src={BUFFALO.wave} alt="" className="w-[140px] h-[140px] object-contain" />
        <div className="text-[20px] font-extrabold text-ink leading-tight mt-5">
          {loadFailed ? "Couldn't load your progress" : "Let's find your level"}
        </div>
        <p className="text-[14px] text-neutral-500 leading-relaxed mt-1.5 mb-0">
          {loadFailed
            ? 'Something went wrong loading your levels. Refresh to try again.'
            : placementUrl
              ? `A 10 to 12 question check finds your level in ${domain.full}.`
              : startReal
                ? `Jump in — your first session finds your level in ${domain.full}.`
                : `A short diagnostic places you at the right level in ${domain.full}.`}
        </p>
        <Button
          variant="primary"
          // !px-[48px] keeps this button exactly as approved: until 2026-10-06 the
          // shared Button's px-5 rendered as Bootstrap's 3rem (!important), which
          // also beat this px-6. The shared Button is now px-[20px].
          className="h-11 !px-[48px] text-[14px] font-bold rounded-[12px] mt-5"
          onClick={() => (loadFailed
            ? window.location.reload()
            : navigate(placementUrl || (startReal ? engineSessionUrl(domainKey) : '/diagnostic')))}
        >
          {loadFailed ? 'Refresh' : placementUrl ? 'Find your level' : startReal ? 'Start practicing' : 'Take the diagnostic'}
        </Button>
      </div>
    );
  }

  const guide = DOMAIN_GUIDES[domainKey];
  const info = levelInfo(domainKey, viewLevel);
  const isCurrent = viewLevel === prog.level;
  const isPast = viewLevel < prog.level;

  // The class unit assignment for THIS domain (declared here, above every use —
  // the road's due markers below reuse it).
  // Multiple classes can assign the SAME domain (Sandy 2026-08-09): the road
  // shows the assignment whose next due lands SOONEST — that's the deadline
  // the student has to answer to first.
  const classAsg = (() => {
    const mine = (agenda?.assignments || []).filter((a) => a.domainKey === domainKey);
    if (!mine.length) return null;
    const nextDueMs = (a) => {
      const s = (a.slots || []).find(isOpenAssignmentSlot);
      return s?.due ? dueDateOf(s.due).getTime() : Infinity;
    };
    return [...mine].sort((a, b) => nextDueMs(a) - nextDueMs(b))[0];
  })();

  // The 48h check lock, off the engine profile (checkLockUntil via
  // getMyProgress) but DUE-AWARE (Sandy 2026-08-08): the engine caps every
  // class lock at next-due minus 24h and re-clamps when the session is
  // opened; mirror the same cap on the displayed countdown so a lock armed
  // before the teacher set a due date never SHOWS as outlasting it. Same due
  // semantics as the engine: timed due = the exact moment, date-only due =
  // start of its UTC day (upcoming through the whole day).
  const nextDueAt = (() => {
    const now = Date.now();
    const ds = (classAsg?.slots || [])
      .filter(isOpenAssignmentSlot)
      .map((s) => s.due)
      .filter(Boolean)
      .map((x) => {
        const str = String(x);
        if (str.includes('T')) { const at = Date.parse(str); return { at, end: at }; }
        const at = Date.parse(str + 'T00:00:00Z');
        return { at, end: at + 86400000 };
      })
      .filter((d) => Number.isFinite(d.at) && d.end > now)
      .map((d) => d.at)
      .sort((a, b) => a - b);
    return ds[0] ?? null;
  })();
  const lockUntilMs = (() => {
    if (!rd?.checkLockUntil) return null;
    const t = new Date(rd.checkLockUntil).getTime();
    const cap = nextDueAt != null ? nextDueAt - 24 * 3600000 : null;
    return cap != null && cap < t ? cap : t;
  })();
  const lockActive = isCurrent && lockUntilMs != null && lockUntilMs > lockNow;

  // Stuck/release lifecycle (2026-08-07, via getAssignments.intervention):
  //   open      -> the check is teacher-locked: say so, no Start button.
  //   redo      -> released to relearn: "redo this lesson, then the check".
  //   recheck   -> released straight to (or arrived at) the retake.
  const iv = isCurrent ? (classAsg?.intervention || null) : null;
  const ivStage = iv?.status === 'released' ? iv.stage : null;
  // No class-unit intervention on this domain (self-serve student, or a
  // domain no class actively tracks — user bug 2026-08-19: the page said
  // "Continue · Skill check" while the engine was about to serve the relearn
  // lesson): the PROFILE itself carries the truth. releaseStage covers
  // released-but-not-yet-entered; the open asgLog item covers mid-retry
  // (its kind is literally 'redo' or 'recheck').
  const lastAsg = Array.isArray(rd?.asgLog) && rd.asgLog.length ? rd.asgLog[rd.asgLog.length - 1] : null;
  const profileStage = rd?.releaseStage
    || (lastAsg && !lastAsg.c && (lastAsg.k === 'redo' || lastAsg.k === 'recheck') ? lastAsg.k : null);
  const retryStage = ivStage || (isCurrent ? profileStage : null);
  // Stuck shows from the PROFILE too (rd.stuck), not only the intervention row:
  // right after a failed check the teacher hasn't opened Intervene yet, so no
  // row exists — but the student is already waiting on them.
  const stuckOpen = !retryStage && (iv?.status === 'open' || (isCurrent && !!rd?.stuck));

  // Start/Continue always enters saved practice, or asks the student to sign
  // in. Local test content is only available through signed-out review.
  const sessionUrl = user ? null : testSessionUrl(domainKey, viewLevel);
  const realSessionUrl = realAll && rd?.band != null ? engineSessionUrl(domainKey) : null;
  // Read-only review (Sandy 2026-08-07): real content, answer-or-show-answers,
  // jumpable, and NOTHING stored. Signed-in students get it on past levels
  // (replacing the old sandbox replay) AND on the level they're mid-unit on.
  const reviewUrl = user && engineSessionUrl(domainKey)
    ? `${engineSessionUrl(domainKey)}?review=1&band=${viewLevel}`
    : null;
  const start = () => {
    if (!user) return navigate('/signin');
    if (realSessionUrl && isCurrent) return navigate(realSessionUrl);
    return toast('Refresh your progress and try again');
  };
  const startReview = (target) => {
    if (reviewUrl) return navigate(reviewGateParams(reviewUrl, target));
    if (sessionUrl) return navigate(sessionUrl); // signed-out demo: old sandbox replay
    return toast('Nothing to review here yet');
  };
  // The gating + deep-link params review carries: only DONE road coins are
  // reviewable, and clicking a coin lands review on that exact part.
  // rdone stays in the review player's units (the engine's 10-part count:
  // 4 + 4 + 1 + 1), NOT the simplified road's coin count — the player's
  // gating math predates the merged coins (Sandy 2026-08-12).
  const slugOf = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const engineDoneCount = (() => {
    const up = rd?.unitProgress || {};
    return Math.min(4, up.a1 || 0) + Math.min(4, up.a2 || 0) + Math.min(1, up.a3 || 0) + Math.min(1, up.a4 || 0);
  })();
  function reviewGateParams(base, target) {
    let url = base;
    // The student's REAL level + current-unit done prefix ride on EVERY
    // review launch (Sandy 2026-08-18): the review player caps its level-hop
    // dropdown at pband and applies the gate whenever the review band IS the
    // student's own level — so future levels and not-yet-reached parts never
    // open, no matter which level the review started from.
    if (rd?.band != null) url += `&pband=${rd.band}`;
    if (useReal && curUnit) {
      const skills = curUnit.items.map((it) => slugOf(it.label)).join(',');
      url += `&rskills=${encodeURIComponent(skills)}&rdone=${engineDoneCount}`;
    }
    if (target) url += `&rt=${encodeURIComponent(target)}`;
    return url;
  }
  // Road node index -> review target. One coin per skill now: a skill coin
  // reviews its lesson; mixed and the check review themselves. Covers the
  // current unit's window AND past levels' real roads (Sandy 2026-08-14:
  // past-level coins carried no target, so review ignored the click and
  // always opened at the first lesson).
  function reviewTargetOf(n) {
    if (n.isAdvance) return null;
    if (useReal && curUnit) {
      const mixedI = curUnit.items.length;
      if (n.i === mixedI) return 'mixed';
      if (n.i === mixedI + 1) return 'check';
      const it = curUnit.items[n.i];
      if (!it) return null;
      return `learn:${slugOf(it.label)}:lesson`;
    }
    if (usePastReal) {
      // Past level shows EVERY unit at that band: walk the unit blocks
      // (items..., mixed, check) to the clicked node.
      let base = 0;
      for (const u of viewUnits) {
        if (n.i < base + u.items.length) {
          const it = u.items[n.i - base];
          return it ? `learn:${slugOf(it.label)}:lesson` : null;
        }
        if (n.i === base + u.items.length) return 'mixed';
        if (n.i === base + u.items.length + 1) return 'check';
        base += u.items.length + 2;
      }
    }
    return null;
  }

  // The road: the CURRENT unit's parts only — REAL window when we have it
  // (the student's own current band), the demo ladder otherwise. A signed-in
  // student whose unit windows didn't load (fetch failed / no content) gets an
  // honest empty road with a note, never the demo mock's parts.
  const useReal = !!(curUnit && isCurrentReal());
  function isCurrentReal() { return !!realAll && rd?.band != null && viewLevel === rd.band; }
  // Past level with real unit windows loaded: render the same one-coin-per-skill
  // road as the current level. While that fetch is pending the road holds EMPTY
  // (mock parts only remain for signed-out demos, future levels, and the rare
  // failed/empty fetch).
  const usePastReal = isPast && !!realAll && Array.isArray(viewUnits) && viewUnits.length > 0;
  const pastPending = isPast && !!realAll && rd?.band != null && viewUnits === null;
  const roadMissing = !!realAll && rd?.band != null && viewLevel === rd.band && !curUnit;
  const parts = useReal ? partsFromUnits([curUnit])
    : usePastReal ? partsFromUnits(viewUnits)
      : pastPending || roadMissing ? [] : partsFor(domainKey, viewLevel);
  // Retry loop anchors (released redo/recheck only, real road only): the
  // lesson being redone is the profile's stuckWeakTop; if that skill is not in
  // this unit's window, fall back to the unit's LAST lesson. For skill j the
  // lesson node is j*4 + 2; the Skill check node is items*4 + 1.
  let retryLoop = null;
  if (useReal && retryStage) {
    let j = curUnit.items.findIndex((it) => it.id === rd?.stuckWeakTop);
    if (j < 0) j = curUnit.items.length - 1;
    // One coin per skill: the skill coin IS the lesson node now.
    retryLoop = { lessonIdx: j, checkIdx: curUnit.items.length + 1 };
  }
  const moreUnitsAtBand = useReal && curUnitIdx < realUnits.length - 1;
  // More units left at this band -> the check finishes THIS unit and the road
  // refreshes to the next window at the same level. Only the band's last unit
  // unlocks the next level.
  const advanceLabel = moreUnitsAtBand
    ? `Finish Unit ${curUnit.n} · next unit unlocks`
    : viewLevel >= MAX_LEVEL ? 'Domain complete' : `Unlock Level ${viewLevel + 1}`;
  // While a past level's real units load, the road holds fully empty — even the
  // advance flag — so nothing flashes before the real coins land.
  const nodes = pastPending ? [] : [
    ...parts.map((p, i) => ({ ...p, i })),
    {
      key: 'advance',
      label: advanceLabel,
      blurb: 'Pass the mastery check to advance',
      i: parts.length,
      isAdvance: true,
    },
  ];
  const statusOf = (n) => {
    if (n.isAdvance) return isPast ? 'done' : 'locked';
    if (isPast) return 'done';
    if (!isCurrent) return 'locked';
    // Released retry rewires the road (Sandy 2026-08-07): during the redo the
    // RELEARN LESSON is the current coin and the check wears a LOCK until the
    // lesson is done; the recheck stage flips the check back to current.
    if (retryLoop) {
      if (retryStage === 'redo') {
        if (n.i === retryLoop.lessonIdx) return 'current';
        if (n.i === retryLoop.checkIdx) return 'locked';
      } else if (retryStage === 'recheck' && n.i === retryLoop.checkIdx) {
        return 'current';
      }
    }
    if (stuckOpen && n.i === prog.part) return 'locked'; // teacher-locked check
    if (n.i < prog.part) return 'done';
    if (n.i === prog.part) return 'current';
    return 'locked';
  };

  const currentParts = useReal ? parts : roadMissing ? [] : partsFor(domainKey, prog.level);
  const currentPart = currentParts[prog.part] || currentParts[0] || null;

  // Due markers: on the class-unit domain's CURRENT level, each assignment
  // plants a flag after the part that completes it. On the REAL road the
  // current unit's four assignments land at fixed offsets in its segment.
  const dueMarkers = {};
  if (useReal) {
    // The unit's four assignment BREAK POINTS always show on the road — the
    // segment ends (each skill's Practice, then Mixed practice, then the
    // check) — even solo or with no due date, so the 4-assignment structure
    // reads while working. When a class unit binds due dates, each surviving
    // due attaches to the student's NEXT assignment in order (getAssignments
    // slots — a late joiner's past dues are N/A and bind nothing), and that
    // break point carries the date + done/current state.
    const twoSkills = curUnit.items.length > 1;
    // One coin per skill: each assignment's break point is its own coin.
    const endIdx = [0, twoSkills ? 1 : null, curUnit.items.length, curUnit.items.length + 1];
    const CANON = ['Lesson', 'Lesson', 'Mixed practice', 'Skill check'];
    // Only REMAINING dues attach to the road (Sandy 2026-08-07): a finished
    // slot's due is history — it lives in the gradebook, locked to the work
    // that fulfilled it, and never re-shows on later assignments. The k-th
    // remaining due binds the student's k-th UPCOMING assignment, starting
    // from their first unfinished one on this road.
    const up2 = rd?.unitProgress || {};
    const ordDone = [Number(up2.a1 || 0) >= 4, Number(up2.a2 || 0) >= 4, Number(up2.a3 || 0) >= 1, Number(up2.a4 || 0) >= 1];
    const firstOpen = ordDone.indexOf(false) === -1 ? 4 : ordDone.indexOf(false);
    const remaining = (classAsg?.slots || []).filter(isOpenAssignmentSlot);
    for (let o = 0; o < 4; o += 1) {
      if (endIdx[o] == null) continue;
      const k = o - firstOpen;
      const s = k >= 0 && k < remaining.length ? remaining[k] : null;
      // s.due may be date-only or a full ISO datetime (2026-08-07): `due` is
      // the Date it renders as, `dueRaw` keeps the stored string so the marker
      // can show the time, `past` = the due has CLOSED (dueEndMs).
      const due = s && s.due ? dueDateOf(s.due) : null;
      dueMarkers[endIdx[o]] = {
        key: `bp${o}`,
        label: (s && s.label) || CANON[o],
        due,
        dueRaw: s && s.due ? s.due : null,
        past: !!due && dueEndMs(s.due) < Date.now(),
        done: undefined,
        current: !!(s && s.current),
      };
    }
  } else if (!useReal && !user && isCurrent) {
    // Mock due markers back the signed-out demo only.
    unitAssignmentsFor(domainKey).forEach((a) => {
      const idx = assignmentPartIndex(domainKey, viewLevel, a.key);
      if (idx >= 0) dueMarkers[idx] = a;
    });
  }

  const A = SUBJECT_ACCENTS[subject] || SUBJECT_ACCENTS.math;
  return (
    <>
      <div
        className="pb-10 w-full grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 lg:gap-10 items-start"
        style={{
          '--sdv3-ring-light': A.ringLight, '--sdv3-ring-dark': A.main,
          '--sdv3-accent-soft': A.soft,
        }}
      >
        {/* Left: what this domain is, in plain words + the level ladder.
            Brilliant-proportioned: wide card, big art, big title. */}
        <div className={CARD + ' p-6 lg:sticky lg:top-5'} data-tour="about">
          <img src={DOMAIN_TOOL_ART[domainKey]} alt={DOMAIN_TOOL_TAGLINES[domainKey] || ''} className="w-40 h-40 object-contain -ml-2" />
          <div className="text-[24px] font-extrabold text-ink leading-tight mt-2">{domain.full}</div>
          <p className="text-[15px] text-neutral-700 leading-relaxed mt-2 mb-0">{guide?.whatIs}</p>

          {/* Real accounts: the unit status renders from the REAL assignment
              (slots done / next due). The mock line only backs signed-out demos. */}
          {(realAll ? !!classAsg : STUDENT.active.domain === domainKey) && (
            <div className="border-t border-neutral-100 mt-4 pt-4">
              {realAll ? <UnitStatus assignment={classAsg} /> : <UnitStatus detail />}
            </div>
          )}

          <div className="border-t border-neutral-100 mt-4 pt-4" data-tour="levels">
            <div className="text-[11px] font-bold uppercase tracking-wide text-neutral-500 mb-2">Your level</div>
            <div className="flex items-center gap-1 flex-wrap">
              {/* levels below the content floor (no Level 1 in info/craft/
                  expression) simply don't render — nothing dead to click */}
              {Array.from({ length: MAX_LEVEL }, (_, i) => i + 1).filter((n) => n >= minLevel).map((n) => {
                const done = n < prog.level;
                const current = n === prog.level;
                return (
                  <button
                    key={n}
                    onClick={() => setViewLevel(n)}
                    aria-label={`Level ${n}`}
                    className={
                      'w-7 h-6 rounded-[9px] flex items-center justify-center text-[11px] font-bold cursor-pointer ' +
                      (current
                        ? 'border-none '
                        : done
                          ? 'bg-white text-success-600 border border-neutral-300 '
                          : 'bg-neutral-100 text-neutral-400 border-none ') +
                      'sdv3-key' + (n === viewLevel ? ' sdv3-key-raised' : '')
                    }
                    style={{
                      ...(current ? { background: A.main, color: A.mainText } : null),
                      '--sdv3-btn-rim': current ? A.btnRim : done ? '#cfcfcf' : '#c2c2c2',
                    }}
                  >
                    {done ? <Check size={13} strokeWidth={3} /> : n}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: the road for the viewed level. */}
        <div className="relative min-w-0" data-tour="road">
          <div className="w-full max-w-[380px] rounded-[12px] border-2 bg-white px-4 py-2.5" style={{ borderColor: A.line }}>
            <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: A.main }}>Level {viewLevel}</div>
            {/* Just which unit this is (Sandy 2026-08-12) — the skills are
                the coins right below, no need to name or count them here. */}
            <div className="text-[16px] font-extrabold text-ink leading-snug">
              {useReal
                ? `Unit ${curUnit.n} of ${realUnits.length}`
                : roadMissing ? domain.full : info.title}
            </div>
          </div>

          <div className="relative pb-4" ref={roadRef}>
            {/* Unit windows unavailable (fetch failed / no content yet): say
                so — the Start button below still launches the real engine. */}
            {roadMissing && (
              <div className="mt-6 text-center text-[13px] font-medium text-neutral-500">
                We couldn't load this level's road right now. You can still practice below.
              </div>
            )}
            <RoadLine containerRef={roadRef} count={nodes.length} />
            {nodes.map((n) => {
              // A slot's due date rides ITS coin's label now (no separate
              // break rows). Done slots drop their date — the coin already
              // says done. Red = past due or under a day out.
              const m = !n.isAdvance ? dueMarkers[n.i] : null;
              const mDone = m && (m.done !== undefined ? m.done : statusOf(n) === 'done');
              const endMs = m && (m.dueRaw ? dueEndMs(m.dueRaw) : m.due ? m.due.getTime() : null);
              const due = m && !mDone && m.due
                ? {
                    text: shortDue(m.dueRaw || m.due),
                    past: !!m.past,
                    urgent: !!m.past || (endMs != null && endMs - Date.now() < 48 * 3600000),
                  }
                : null;
              return (
                <div key={n.key} data-road-i={n.i} className="relative z-[1]">
                  <PathNode
                    part={{ ...n, due }}
                    status={statusOf(n)}
                    isLast={n.isAdvance && viewLevel >= MAX_LEVEL}
                    // Done coins open READ-ONLY review AT that part (Sandy
                    // 2026-08-08); the current coin still resumes the real
                    // session, asking signed-out visitors to sign in.
                    onClick={statusOf(n) === 'done' && reviewUrl
                      ? () => startReview(reviewTargetOf(n))
                      : start}
                  />
                </div>
              );
            })}
            {/* Released after a failed check: the faint loop the student is on,
                drawn over the road — (1) back up to the lesson, (2) back down
                to the check. PARKED (Sandy 2026-08-07: not rendering on their
                setup; road padlock/current states carry the story for now). */}
            {false && retryLoop && (
              <RetryLoopOverlay
                containerRef={roadRef}
                lessonIdx={retryLoop.lessonIdx}
                checkIdx={retryLoop.checkIdx}
                stage={retryStage}
              />
            )}
            {/* Level-up rule, stated once: with more units left at this level,
                passing this unit's check opens the NEXT unit, not the next level. */}
            {useReal && moreUnitsAtBand && (
              <div className="mt-3 pl-2 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                Pass every unit at this level to move up
              </div>
            )}
          </div>

          {/* Bottom action card, Brilliant-style: the part name centered and big,
              one button under it. No mini coin — the road already shows state. */}
          {/* z-10: the road's coins are z-[1], and past levels' real roads are
              long enough to scroll under this card — it must paint above them. */}
          {/* A locked future level has no action to pin. Keep its message in
              the road flow so the floating Buffy button cannot cover it. */}
          <div className={`${isCurrent || isPast ? 'sticky bottom-4 ' : ''}z-10 w-full max-w-[360px] bg-white border border-neutral-200 rounded-[12px] shadow-[0_12px_32px_rgba(0,0,0,.12)] px-5 py-4 mt-2`} data-tour="action">
            <div className="mb-3 text-center text-[18px] font-extrabold text-ink leading-snug">
              {isCurrent && (
                stuckOpen ? 'Skill check locked'
                  : retryStage === 'redo' ? 'Redo this lesson'
                    : retryStage === 'recheck' ? 'Skill check unlocked'
                      : lockActive ? 'Skill check locked'
                        : currentPart ? partRowLabel(currentPart) : domain.full
              )}
              {isPast && 'Level complete'}
              {!isCurrent && !isPast && `Finish Level ${prog.level} to unlock`}
            </div>
            {stuckOpen && (
              <p className="m-0 mb-3 text-center text-[13px] text-neutral-600 leading-relaxed">
                You did the work. This retake just needs your teacher: ask them to unlock it.
              </p>
            )}
            {retryStage === 'redo' && (
              <p className="m-0 mb-3 text-center text-[13px] text-neutral-600 leading-relaxed">
                Redo this lesson and the skill check unlocks again. Try again, you have got this!
              </p>
            )}
            {retryStage === 'recheck' && (
              <p className="m-0 mb-3 text-center text-[13px] text-neutral-600 leading-relaxed">
                Your retake is open. Take your time and show what you know.
              </p>
            )}
            {!stuckOpen && !retryStage && lockActive && (
              <div className="mb-3 flex justify-center">
                <CheckLockTimer until={new Date(lockUntilMs).toISOString()} />
              </div>
            )}
            {isCurrent && !stuckOpen && (
              <Button
                variant="primary"
                className="w-full h-11 text-[14px] font-bold rounded-[16px] sdv3-btn3d hover:brightness-95"
                style={{ background: A.btn, borderColor: A.btn, borderRadius: 16, '--sdv3-btn-rim': A.btnRim }}
                onClick={start}
              >
                {/* span carries the text color — the legacy Bootstrap sheet's
                    `.text-white !important` on the button would beat an inline
                    color set on the button itself */}
                <span style={{ color: A.btnText }}>
                  {retryStage === 'redo' ? 'Redo the lesson' : retryStage === 'recheck' ? 'Retake the check' : prog.part === 0 ? 'Start' : 'Continue'}
                </span>
              </Button>
            )}
            {isPast && (
              <Button variant="secondary" className="w-full h-11 text-[14px] font-bold rounded-[16px] sdv3-btn3d" style={{ borderRadius: 16, '--sdv3-btn-rim': '#d4d4d4' }} onClick={() => startReview()}>
                Review this level
              </Button>
            )}
            {/* mid-unit review: look back at anything without touching the
                real session's saved spot (nothing in review is stored) */}
            {isCurrent && !stuckOpen && reviewUrl && (
              <Button variant="secondary" className="w-full h-9 mt-2 text-[13px] font-bold rounded-[14px]" style={{ borderRadius: 14 }} onClick={() => startReview()}>
                Review this level
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// The old standalone route: /student-dashboard/:subject/:domainKey now lives
// under the home page's tabs. Redirect carries ?level / ?unplaced through so
// ladder deep links and the unplaced preview keep working.
export default function StudentDomainPage() {
  const { subject, domainKey } = useParams();
  const { search } = useLocation();
  if (!DOMAINS[subject]) return <Navigate to="/student-dashboard" replace />;
  const qs = new URLSearchParams(search);
  qs.set('tab', subject);
  qs.set('domain', domainKey);
  return <Navigate to={`/student-dashboard?${qs.toString()}`} replace />;
}
